<template>
  <EditorNodeWrapper
    :class="['pricing-table-container', blockWidthClass]"
    @observe-node-visibility="$emit('observe-node-visibility', { ...$event, nodeId: node.id })"
  >
    <div class="pricing-table-content">
      <PricingTableSection
        v-for="section in sections"
        :key="section.id"
        :node-id="node.id"
        :pricing-table="node"
        :section-id="section.id"
        :section="section"
        :currency-settings="node.currencySettings"
        :is-pre-calculated="node.preCalculated"
        :merge-values="mergeValues"
        :fields="fieldsWithInput || []"
        :recipient-input="recipientInput"
        :recipient="recipient"
        :allowed-to-edit-after-partially-signed="allowedToEditAfterPartiallySigned"
        :summary-values="getSummaryValues(node, [])"
        @update-input-value="$emit('update-input-value', $event)"
      />
      <div v-if="isSummaryEnabled" class="pricing-table-summary-container">
        <PricingTablePriceSummaryNew :pricing-table="node" :fields="fieldsWithInput" />
      </div>
    </div>
  </EditorNodeWrapper>
</template>
<script lang="ts" setup>
import {
  getSummaryValues,
  PricingTableHelper,
  usePricingTableSummary,
} from '@getaccept/editor-lib';
import type {
  PricingTable,
  MergeValues,
  PricingTableSection as IPricingTableSection,
  EBlockWidth,
} from '@getaccept/editor-lib';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { computed } from 'vue';
import EditorNodeWrapper from '../EditorNodeWrapper.vue';
import PricingTableSection from './PricingTableSection.vue';
import PricingTablePriceSummaryNew from './PricingTablePriceSummaryNew.vue';

interface Props {
  node: PricingTable;
  sections: IPricingTableSection[];
  mergeValues: MergeValues;
  blockWidthClass?: EBlockWidth;
  recipient?: Recipient;
  recipientInput: EditorRecipientInput[];
  allowedToEditAfterPartiallySigned?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  allowedToEditAfterPartiallySigned: false,
  recipient: undefined,
  blockWidthClass: undefined,
});

defineEmits<{
  'update-input-value': [value: { id: string; value: string | undefined }];
  'observe-node-visibility': [value: { visible: boolean; nodeId: string }];
}>();

const fieldsWithInput = computed(() =>
  PricingTableHelper.fieldsWithRecipientInput(props.node.fields, props.recipientInput)
);

const { isSummaryEnabled } = usePricingTableSummary(
  computed(() => props.node),
  fieldsWithInput
);
</script>
<style lang="scss" scoped>
@import '@getaccept/editor-lib/src/scss/editor-node-pricing-table';
</style>
